import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import { GkRowCard, GkCard, categoryName } from "@/components/feature/ArticleCards";
import { gkArticles } from "@/mocks/news";
import { gkCategories } from "@/mocks/categories";

const SITE = "https://dqxuaad.cn";

export default function NewsList() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") || "";
  const catParam = params.get("cat") || "";
  const [localQ, setLocalQ] = useState(query);

  const tabs = [{ slug: "", name: "全部瓜田" }, ...gkCategories.map((c) => ({ slug: c.slug, name: c.name }))];

  const filtered = useMemo(() => {
    let list = gkArticles;
    if (catParam) list = list.filter((a) => a.category === catParam);
    if (query) {
      const kw = query.toLowerCase();
      list = list.filter((a) => (a.title + a.summary + a.tags.join("")).toLowerCase().includes(kw));
    }
    return list;
  }, [query, catParam]);

  const applyCat = (slug: string) => {
    const next = new URLSearchParams(params);
    if (slug) next.set("cat", slug);
    else next.delete("cat");
    setParams(next);
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = localQ.trim();
    const next = new URLSearchParams(params);
    if (kw) next.set("q", kw);
    else next.delete("q");
    setParams(next);
  };

  return (
    <>
      <Seo
        title="新鲜瓜速递 - 看吃瓜最新爆料与每日瓜料"
        description="看吃瓜新鲜瓜速递栏目，汇总最新爆料与每日瓜料，可跨栏目搜索你想找的今日新瓜与今日热点。"
        canonicalPath="/news"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "看吃瓜", item: SITE },
              { "@type": "ListItem", position: 2, name: "新鲜瓜速递", item: `${SITE}/news` },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "新鲜瓜速递",
            description: "看吃瓜最新爆料与每日瓜料速递列表",
            url: `${SITE}/news`,
          },
        ]}
      />

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-8 md:py-12">
        {/* Page header */}
        <nav aria-label="面包屑" className="mb-6 flex items-center gap-2 text-xs text-foreground-500">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700">新鲜瓜速递</span>
        </nav>

        <div className="mb-8 rounded-3xl bg-gradient-to-br from-primary-500 to-accent-600 p-6 md:p-10">
          <p className="mb-2 flex items-center gap-2 text-xs font-medium tracking-widest text-background-50/90">
            <i className="ri-flashlight-fill"></i>每日瓜料准时更新
          </p>
          <h1 className="font-heading text-3xl font-bold text-background-50 md:text-4xl">新鲜瓜速递</h1>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-background-50/90 md:text-base">最新爆料、今日新瓜与全网热点的速递汇总，支持跨栏目搜索，快速扫一眼今天值得吃的瓜。</p>

          <form onSubmit={submitSearch} className="mt-6 flex max-w-xl items-center gap-2 rounded-full bg-background-50 p-1.5 shadow-soft">
            <i className="ri-search-line pl-3 text-foreground-400"></i>
            <input
              value={localQ}
              onChange={(e) => setLocalQ(e.target.value)}
              placeholder="搜索今日新瓜、最新爆料关键词…"
              className="flex-1 bg-transparent px-2 py-2 text-sm outline-none placeholder:text-foreground-400"
            />
            <button type="submit" className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600">搜索瓜料</button>
          </form>
        </div>

        {/* Filter tabs */}
        <div className="mb-8 flex flex-wrap gap-2">
          {tabs.map((t) => (
            <button
              key={t.slug}
              type="button"
              onClick={() => applyCat(t.slug)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${catParam === t.slug ? "bg-primary-500 text-background-50" : "bg-background-50 text-foreground-700 ring-1 ring-background-200 hover:text-primary-600"}`}
            >
              {t.name}
            </button>
          ))}
        </div>

        {query && (
          <p className="mb-6 text-sm text-foreground-600">
            正在搜索 <span className="font-semibold text-primary-600">“{query}”</span>，共找到 {filtered.length} 条相关瓜料。
            <button type="button" onClick={() => { setLocalQ(""); const next = new URLSearchParams(params); next.delete("q"); setParams(next); }} className="ml-2 text-primary-600 hover:underline">清除</button>
          </p>
        )}

        {filtered.length === 0 ? (
          <div className="rounded-3xl border border-background-200 bg-background-50 p-12 text-center">
            <i className="ri-folder-forbid-line text-5xl text-background-400"></i>
            <p className="mt-4 text-foreground-700">没找到对应的瓜料，换个关键词再试试吧。</p>
            <Link to="/news" className="mt-4 inline-block rounded-full bg-primary-500 px-5 py-2 text-sm text-background-50 hover:bg-primary-600">看看全部瓜田</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {filtered.slice(0, 3).map((a) => (
                <GkCard key={a.id} article={a} imgClass="aspect-[3/2]" />
              ))}
            </div>
            <div className="mt-2 grid grid-cols-1 gap-4">
              {filtered.slice(3).map((a) => (
                <GkRowCard key={a.id} article={a} />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}